/**
 * Cliente HTTP dos avisos do portal (E66.F3, ADR-0071).
 *
 * Espelha `apps/api/types/avisos.AvisosOut`. O contrato carrega o **tipo** do
 * acontecimento e o fato — nunca a frase: o texto é montado na leitura, do
 * catálogo de i18n (ADR-0071 §2.2).
 *
 * `marcarAvisoVisto` devolve a lista JÁ atualizada em vez de 204. O contador de
 * não-lidos é agregado da mesma lista (BR-10), e responder vazio obrigaria o
 * navegador a recontar por conta própria — que é como duas superfícies começam
 * a discordar sobre o mesmo número.
 */
import type { AxiosInstance } from 'axios';

import type { DesfechoDaResposta, TipoDaMensagem } from '@/lib/conversa_do_caso';
import type { SituacaoDoCasoClienteData } from '@/lib/portal/situacao';

/** E150.F1 (ADR-0102 §4): os avisos da conversa, dos dois lados; o portal recebe os da equipe e a
 * equipe recebe os do cliente (`lib/avisos_da_equipe.ts`). */
export type AvisoTipo =
  | 'exame_certificado'
  | 'laudo_homologado'
  | 'laudo_gerado'
  | 'documento_solicitado'
  | 'mensagem_da_equipe'
  | 'mensagem_do_cliente'
  | 'complemento_recebido'
  | 'pedido_respondido';

export interface AvisoData {
  tipo: AvisoTipo;
  /** Identidade do FATO: a assinatura em `exame_certificado`, o caso nos outros. */
  objeto_id: string;
  case_id: string;
  case_display_code: string;
  ccb_number: string | null;
  ocorrido_em: string;
  /**
   * Data civil (AAAA-MM-DD) no fuso do produto, resolvida pelo SERVIDOR.
   * Derivar o dia de `ocorrido_em` no navegador devolveria o dia UTC — a
   * F-E66.F2-02, que o painel das certificações já pagou.
   */
  ocorrido_em_date: string;
  visto: boolean;
  /** E150.F1: na conversa, o que a mensagem é, o desfecho da resposta e o documento pedido. */
  mensagem_tipo?: TipoDaMensagem | null;
  desfecho?: DesfechoDaResposta | null;
  material?: string | null;
  /** E165.F2.T06 (portal 6.8): a situação de AGORA do caso; ausente nos avisos da equipe. */
  situacao?: SituacaoDoCasoClienteData | null;
}

export interface AvisosData {
  itens: AvisoData[];
  nao_vistos: number;
}

/** Avisos alterados (marcados como lidos): o contador do menu relê. */
export const EVENTO_AVISOS_ALTERADOS = 'avisos:alterados';

export async function getAvisos(client: AxiosInstance): Promise<AvisosData> {
  const { data } = await client.get<AvisosData>('/portal/avisos');
  return data;
}

export async function marcarAvisoVisto(
  client: AxiosInstance,
  aviso: { tipo: AvisoTipo; objeto_id: string },
): Promise<AvisosData> {
  const { data } = await client.post<AvisosData>('/portal/avisos/visto', aviso);
  return data;
}
